import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCart } from '@/state/cart/useCart';
import { CartItemTypeEnum } from '../TypeDefs';

// GraphQL query to get saved attendee details
export const GET_MY_ATTENDEE_DETAILS = gql`
  query GetMyAttendeeDetails {
    getMyCheckoutAttendeesDetails {
      _id
      purchaserId
      attendeeId
      eventId
      ticketId
      firstName
      lastName
      email
      customAttributes
      orderId
      cartItemId
      isValidated
      attendeeUser {
        _id
        displayName
        proxyProfilePictureUrl(input: { w: 40, h: 40 })
      }
    }
  }
`;

export interface AttendeeDetail {
  _id?: string;
  attendeeId?: string;
  ticketId: string;
  attendeeIndex: number;
  firstName: string;
  lastName: string;
  email: string;
  customAttributes?: any[];
  /** False when auto-populated via registerMe; user must confirm by saving. */
  isValidated?: boolean | null;
  attendeeUser?: {
    _id: string;
    displayName: string;
    proxyProfilePictureUrl?: string;
  } | null;
}

export interface TicketRequirement {
  ticketId: string;
  ticketName: string;
  quantityNeeded: number;
  attendeeIds: string[];
  completedCount: number;
  isComplete: boolean;
  issues: string[];
}

const isRegistrationCartItem = (item: { type: string }) =>
  item.type === CartItemTypeEnum.EVENTS ||
  item.type === CartItemTypeEnum.COURSE ||
  item.type === CartItemTypeEnum.COLLECTION;

const normalizeRegistrationId = (value: unknown): string =>
  value == null ? '' : String(value).trim();

const getRegistrationScopeId = (item: {
  type: string;
  itemId: string;
  eventId?: string | null;
}) =>
  item.type === CartItemTypeEnum.COLLECTION
    ? normalizeRegistrationId(item.itemId || item.eventId)
    : normalizeRegistrationId(item.eventId || '');

const getRegistrationTicketId = (item: {
  type: string;
  _id: string;
  itemId: string;
  eventId?: string | null;
  eventTicketId?: string | null;
  otherItemId?: string | null;
}) =>
  item.type === CartItemTypeEnum.COLLECTION
    ? normalizeRegistrationId(item.otherItemId || item.eventTicketId)
    : normalizeRegistrationId(item.eventTicketId || item._id);

const attendeeMatchesCartItem = (
  attendee: {
    eventId?: string;
    ticketId?: string;
    cartItemId?: string | null;
  },
  item: {
    type: string;
    _id: string;
    itemId: string;
    eventId?: string | null;
    eventTicketId?: string | null;
    otherItemId?: string | null;
  },
) => {
  const attendeeCartItemId = normalizeRegistrationId(attendee.cartItemId);
  const itemCartId = normalizeRegistrationId(item._id);
  if (attendeeCartItemId && itemCartId && attendeeCartItemId === itemCartId) {
    return true;
  }

  const scopeId = getRegistrationScopeId(item);
  const ticketId = getRegistrationTicketId(item);
  const attendeeEventId = normalizeRegistrationId(attendee.eventId);
  const attendeeTicketId = normalizeRegistrationId(attendee.ticketId);

  if (!scopeId || attendeeEventId !== scopeId) {
    return false;
  }

  if (!ticketId) {
    return true;
  }

  return (
    attendeeTicketId === ticketId ||
    attendeeTicketId === itemCartId
  );
};

const isSavedAttendeeComplete = (attendee: {
  _id?: string;
  attendeeId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  isValidated?: boolean | null;
}): boolean => {
  if (attendee.isValidated === false) {
    return false;
  }

  return !!(
    attendee._id &&
    (attendee.attendeeId ||
      (attendee.firstName?.trim() &&
        attendee.lastName?.trim() &&
        attendee.email?.trim()))
  );
};

export interface AttendeeValidationResult {
  // Overall status
  hasEventTickets: boolean;
  needsAttendeeDetails: boolean;
  isAttendeeDetailsComplete: boolean;
  /** True when any cart registration item has saved attendee details with isValidated: false. */
  hasUnvalidatedAttendees: boolean;
  incompleteEventItems: string[];
  
  // Progress tracking
  totalAttendeesNeeded: number;
  totalAttendeesCompleted: number;
  
  // Detailed breakdown
  ticketRequirements: TicketRequirement[];
  
  // Attendee details for forms
  attendeeDetails: AttendeeDetail[];
  
  // Legacy format for backward compatibility
  validationDetails: {
    totalEventItems: number;
    totalAttendeesNeeded: number;
    totalAttendeesRegistered: number;
    itemValidations: Array<{
      itemName: string;
      itemId: string;
      quantityNeeded: number;
      attendeeIdsCount: number;
      attendeeDataCount: number;
      completedAttendeesCount: number;
      isValid: boolean;
      issues: string[];
    }>;
  };
  
  // Helper methods
  getRequirementsForEvent: (eventId: string) => TicketRequirement[];
  getAttendeeDetailsForEvent: (eventId: string) => AttendeeDetail[];
}

/**
 * Single hook to handle all attendee validation and registration logic
 * Uses cart items as the single source of truth
 */
export const useAttendeeValidation = (): AttendeeValidationResult => {
  const { items } = useCart();
  const { data: attendeeData, refetch } = useQuery(GET_MY_ATTENDEE_DETAILS, {
    fetchPolicy: 'cache-and-network',
  });
  
  // Create a stable key from event items' checkoutAttendeesDetails to detect changes
  // Only refetch when checkoutAttendeesDetails changes, NOT when quantity changes
  // Quantity changes don't affect saved attendee details - only when attendees are assigned/unassigned
  const eventItemsKey = useMemo(() => {
    const registrationItems = (items || []).filter(isRegistrationCartItem);
    return registrationItems.map(item => {
      const attendeeIds = item.checkoutAttendeesDetails || [];
      // Only include item ID and attendee IDs - exclude quantity to prevent unnecessary refetches
      // Create a copy before sorting to avoid mutating read-only arrays
      return `${item._id}:${[...attendeeIds].sort().join(',')}`;
    }).sort().join('|');
  }, [items]);
  
  // Track last key and refetch timestamp to prevent duplicate refetches
  const lastEventItemsKeyRef = React.useRef<string>('');
  const lastRefetchTimeRef = React.useRef<number>(0);
  const refetchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Refetch attendee details ONLY when checkoutAttendeesDetails changes (not quantity)
  React.useEffect(() => {
    if (eventItemsKey && eventItemsKey !== lastEventItemsKeyRef.current) {
      lastEventItemsKeyRef.current = eventItemsKey;
      
      // Debounce refetch to prevent duplicate calls within 500ms
      const now = Date.now();
      const timeSinceLastRefetch = now - lastRefetchTimeRef.current;
      
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
      
      if (timeSinceLastRefetch < 500) {
        // Debounce if called too soon after last refetch
        refetchTimeoutRef.current = setTimeout(() => {
          lastRefetchTimeRef.current = Date.now();
          refetch();
        }, 500 - timeSinceLastRefetch);
      } else {
        // Refetch immediately if enough time has passed
        lastRefetchTimeRef.current = now;
        refetch();
      }
    }
    
    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
      }
    };
  }, [eventItemsKey, refetch]);
  
  const savedAttendeeDetails = attendeeData?.getMyCheckoutAttendeesDetails || [];
  
  return useMemo(() => {
    const relevantItems = (items || []).filter(isRegistrationCartItem);
    
    if (relevantItems.length === 0) {
      return {
        hasEventTickets: false,
        needsAttendeeDetails: false,
        isAttendeeDetailsComplete: true,
        hasUnvalidatedAttendees: false,
        incompleteEventItems: [],
        totalAttendeesNeeded: 0,
        totalAttendeesCompleted: 0,
        ticketRequirements: [],
        attendeeDetails: [],
        validationDetails: {
          totalEventItems: 0,
          totalAttendeesNeeded: 0,
          totalAttendeesRegistered: 0,
          itemValidations: [],
        },
        getRequirementsForEvent: () => [],
        getAttendeeDetailsForEvent: () => [],
      };
    }
    
    // Process each cart item to determine requirements
    const ticketRequirements: TicketRequirement[] = relevantItems.map(item => {
      const attendeeIds = item.checkoutAttendeesDetails || [];
      const issues: string[] = [];
      
      // Find ALL saved attendee details for this event/ticket combination
      // Don't rely solely on checkoutAttendeesDetails array - count from savedAttendeeDetails directly
      // This ensures we get accurate counts even if checkoutAttendeesDetails is stale
      const allAttendeesForThisItem = savedAttendeeDetails.filter((attendee: any) =>
        attendeeMatchesCartItem(attendee, item),
      );
      
      // Also find attendees that match the checkoutAttendeesDetails array (for backward compatibility)
      const attendeesFromArray = savedAttendeeDetails.filter((attendee: any) =>
        attendeeIds.includes(attendee._id)
      );
      
      // Use the larger count to handle both cases (array might be stale, but savedAttendeeDetails is source of truth)
      const attendeesForThisItem = allAttendeesForThisItem.length >= attendeesFromArray.length 
        ? allAttendeesForThisItem 
        : attendeesFromArray;
      
      // Validation checks - compare with quantity needed
      const registeredCount = attendeesForThisItem.length;
      if (registeredCount !== item.quantity) {
        issues.push(`Need ${item.quantity} attendees, have ${registeredCount} registered`);
      }
      
      // Count completed attendees (must have _id, required info, and be validated)
      const completedAttendees = attendeesForThisItem.filter((attendee: any) =>
        isSavedAttendeeComplete(attendee),
      );

      const unvalidatedCount = attendeesForThisItem.filter(
        (attendee: any) => attendee.isValidated === false,
      ).length;

      if (unvalidatedCount > 0) {
        issues.push(
          `${unvalidatedCount} attendee${unvalidatedCount === 1 ? '' : 's'} need confirmation`,
        );
      }

      if (completedAttendees.length < registeredCount) {
        issues.push(`${registeredCount - completedAttendees.length} attendees missing required info`);
      }
      
      // Use the actual attendee IDs from savedAttendeeDetails, not just from checkoutAttendeesDetails
      const actualAttendeeIds = attendeesForThisItem.map((a: any) => a._id).filter(Boolean);
      
      return {
        ticketId: getRegistrationTicketId(item),
        ticketName: item.name || 'Unknown Ticket',
        quantityNeeded: item.quantity,
        attendeeIds: actualAttendeeIds.length > 0 ? actualAttendeeIds : attendeeIds, // Prefer actual IDs
        completedCount: completedAttendees.length,
        isComplete: issues.length === 0 && completedAttendees.length === item.quantity,
        issues,
      };
    });
    
    // Generate attendee details for forms
    const attendeeDetails: AttendeeDetail[] = [];
    let attendeeIndex = 0;
    
    relevantItems.forEach(item => {
      const attendeeIds = item.checkoutAttendeesDetails || [];
      
      // Find ALL saved attendees for this ticket (more reliable than just checking array)
      const allSavedAttendeesForTicket = savedAttendeeDetails.filter((saved: any) =>
        attendeeMatchesCartItem(saved, item),
      );
      
      // Create a set of attendee IDs that are already matched
      const matchedAttendeeIds = new Set<string>();
      
      // Create attendee details for the quantity needed
      for (let i = 0; i < item.quantity; i++) {
        const attendeeId = attendeeIds[i];
        let existingAttendee = null;
        
        // First, try to find by attendeeId from array (preserves order)
        if (attendeeId) {
          existingAttendee = savedAttendeeDetails.find((saved: any) => saved._id === attendeeId);
          if (existingAttendee) {
            matchedAttendeeIds.add(existingAttendee._id);
          }
        }
        
        // If not found in array, find an unmatched saved attendee for this ticket
        if (!existingAttendee) {
          existingAttendee = allSavedAttendeesForTicket.find(
            (saved: any) => !matchedAttendeeIds.has(saved._id)
          );
          if (existingAttendee) {
            matchedAttendeeIds.add(existingAttendee._id);
          }
        }
        
        const attendeeDetail = {
          _id: existingAttendee?._id,
          attendeeId: existingAttendee?.attendeeId,
          ticketId: getRegistrationTicketId(item),
          attendeeIndex: attendeeIndex++,
          // Prioritize stored firstName/lastName over attendeeUser.displayName
          // This ensures we use the name that was actually entered/saved
          firstName: existingAttendee?.firstName || 
                     (existingAttendee?.attendeeUser?.displayName?.split(' ')[0]) || 
                     '',
          lastName: existingAttendee?.lastName || 
                    (existingAttendee?.attendeeUser?.displayName?.split(' ').slice(1).join(' ')) || 
                    '',
          email: existingAttendee?.email || '', // Always use stored email, not from attendeeUser for privacy
          customAttributes: existingAttendee?.customAttributes || [],
          isValidated: existingAttendee?.isValidated ?? undefined,
          // Add user details for display
          attendeeUser: existingAttendee?.attendeeUser || null,
        };
        
        attendeeDetails.push(attendeeDetail);
      }
    });
    
    const hasUnvalidatedAttendees = relevantItems.some((item) =>
      savedAttendeeDetails.some(
        (attendee: any) =>
          attendee.isValidated === false &&
          attendeeMatchesCartItem(attendee, item),
      ),
    );

    // Calculate totals
    const totalAttendeesNeeded = ticketRequirements.reduce((sum, req) => sum + req.quantityNeeded, 0);
    const totalAttendeesCompleted = ticketRequirements.reduce((sum, req) => sum + req.completedCount, 0);
    const isComplete =
      !hasUnvalidatedAttendees &&
      ticketRequirements.every((req) => req.isComplete);
    
    // Legacy format for backward compatibility
    const itemValidations = ticketRequirements.map(req => ({
      itemName: req.ticketName,
      itemId: req.ticketId,
      quantityNeeded: req.quantityNeeded,
      attendeeIdsCount: req.attendeeIds.length,
      attendeeDataCount: req.attendeeIds.length,
      completedAttendeesCount: req.completedCount,
      isValid: req.isComplete,
      issues: req.issues,
    }));
    
    return {
      hasEventTickets: true,
      needsAttendeeDetails: true,
      isAttendeeDetailsComplete: isComplete,
      hasUnvalidatedAttendees,
      incompleteEventItems: ticketRequirements
        .filter(req => !req.isComplete)
        .map(req => req.ticketName),
      totalAttendeesNeeded,
      totalAttendeesCompleted,
      ticketRequirements,
      attendeeDetails,
      validationDetails: {
        totalEventItems: relevantItems.length,
        totalAttendeesNeeded,
        totalAttendeesRegistered: totalAttendeesCompleted,
        itemValidations,
      },
      
      // Helper methods
      getRequirementsForEvent: (targetEventId: string) => {
        const normalizedTargetEventId = normalizeRegistrationId(targetEventId);
        if (!normalizedTargetEventId) {
          return [];
        }

        return ticketRequirements.filter(req => {
          const cartItem = relevantItems.find(
            item => getRegistrationTicketId(item) === req.ticketId,
          );

          if (cartItem) {
            return getRegistrationScopeId(cartItem) === normalizedTargetEventId;
          }

          return req.attendeeIds.some(id => {
            const attendee = savedAttendeeDetails.find((a: any) => a._id === id);
            return normalizeRegistrationId(attendee?.eventId) === normalizedTargetEventId;
          });
        });
      },

      getAttendeeDetailsForEvent: (targetEventId: string) => {
        return attendeeDetails.filter(detail => {
          const cartItem = relevantItems.find(
            item => getRegistrationTicketId(item) === detail.ticketId,
          );

          if (cartItem) {
            return getRegistrationScopeId(cartItem) === targetEventId;
          }

          const attendee = savedAttendeeDetails.find((a: any) => a._id === detail._id);
          return attendee?.eventId === targetEventId;
        });
      },
    };
  }, [items, savedAttendeeDetails]);
};

